/**
 * Centralized Firebase API Service
 * 
 * This module consolidates all Firebase Cloud Functions calls to avoid duplication
 * across the codebase. Import from here instead of declaring functions locally.
 */

import { functions } from '../firebase';
import firebase from 'firebase/compat/app';

// Re-export the functions object for direct access if needed (edge cases)
export { functions };

// ============================================================================
// UPLOAD / STORAGE APIs
// ============================================================================

/**
 * Get a signed upload URL for Firebase Storage
 */
/**
 * Get a signed upload URL for Firebase Storage
 */
export const getUploadUrl = functions.httpsCallable('getUploadUrl');

/**
 * Get a direct upload URL for Cloudflare Stream
 */
export const getStreamUploadUrl = functions.httpsCallable('getStreamUploadUrl');

/**
 * Get a presigned URL for reading files
 */
export const getPresignedUrl = functions.httpsCallable('getPresignedUrl');

/**
 * Get a signed upload URL for Cloudflare R2 storage
 */
export const getR2UploadUrl = functions.httpsCallable('getR2UploadUrl');

/**
 * Delete a file from Firebase Storage
 */
export const deleteFirebaseFile = functions.httpsCallable('deleteFirebaseFile');

/**
 * Delete a file from R2 Storage
 */
export const deleteR2File = functions.httpsCallable('deleteR2File');

/**
 * Initiate R2 Multipart Upload
 */
export const initiateMultipartUpload = functions.httpsCallable('initiateMultipartUpload');

/**
 * Get Signed URLs for R2 Multipart chunks
 */
export const getMultipartUploadUrls = functions.httpsCallable('getMultipartUploadUrls');

/**
 * Complete R2 Multipart Upload
 */
export const completeMultipartUpload = functions.httpsCallable('completeMultipartUpload');

// ============================================================================
// AI / MEDIA PROCESSING APIs
// ============================================================================

/**
 * Call Gemini AI for various operations (editing plans, transcription analysis, etc.)
 */
export const callGemini = functions.httpsCallable('callGemini', { timeout: 3600000 });

/**
 * Transcribe audio to text using Whisper or GPT-4o
 */
export const transcribeAudio = functions.httpsCallable('transcribeAudio', { timeout: 3600000 });

/**
 * Translate subtitles to another language
 */
export const translateSubtitles = functions.httpsCallable('translateSubtitles', { timeout: 3600000 });

/**
 * Batch translate subtitles to multiple languages
 */
export const translateSubtitlesBatch = functions.httpsCallable('translateSubtitlesBatch', { timeout: 3600000 });

/**
 * 🔬 BETA: Google Translate → Gemini lexicon verification
 */
export const translateSubtitlesBeta = functions.httpsCallable('translateSubtitlesBeta', { timeout: 3600000 });

/**
 * ✨ CONSTANCE SPECIFIC: Isolated Gemini (Replicate)
 */
export const callGeminiConstance = functions.httpsCallable('callGeminiConstance', { timeout: 3600000 });

/**
 * ✨ CONSTANCE SPECIFIC: Isolated Transcription (Replicate + LVMH Split)
 */
export const transcribeAudioConstance = functions.httpsCallable('transcribeAudioConstance', { timeout: 3600000 });

/**
 * Enhance audio quality using AI
 */
export const enhanceAudio = functions.httpsCallable('enhanceAudio', { timeout: 3600000 });

/**
 * Enhance video using AI (starts async job)
 */
export const enhanceVideo = functions.httpsCallable('enhanceVideo', { timeout: 3600000 });

/**
 * Check status of video enhancement job
 */
export const checkEnhanceStatus = functions.httpsCallable('checkEnhanceStatus', { timeout: 3600000 });

/**
 * Toggle eye contact on video
 */
export const toggleEyeContact = functions.httpsCallable('toggleEyeContact', { timeout: 3600000 });

// ============================================================================
// EXPORT APIs
// ============================================================================

/**
 * Export video (legacy client-triggered export)
 */
export const exportVideo = functions.httpsCallable('exportVideo', { timeout: 3600000 });

/**
 * Process and add media to library (intro/outro, audio, etc.)
 */
export const processAndAddToLibrary = functions.httpsCallable('processAndAddToLibrary', { timeout: 300000 });

/**
 * Helper to track Replicate Usage
 */
export const trackReplicateUsage = async (tokens = 1) => {
    try {
        const user = firebase.auth().currentUser;
        if (!user) return;
        const tokenStr = await user.getIdToken();
        const exportServiceUrl = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';
        await fetch(`${exportServiceUrl}/admin/track-replicate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokenStr}`
            },
            body: JSON.stringify({ tokens })
        });
    } catch (err) {
        console.warn('Failed to track Replicate usage:', err);
    }
};

/**
 * Re-encode video using Replicate (Async Start)
 */
const _reencodeVideo = functions.httpsCallable('reencodeVideo');
export const reencodeVideo = async (data: any) => {
    const res = await _reencodeVideo(data);
    trackReplicateUsage(1).catch(() => {});
    return res;
};

/**
 * Check Replicate Prediction Status (Polling)
 */
export const checkReplicateStatus = functions.httpsCallable('checkReplicateStatus');

/**
 * Burn subtitles into video using Replicate
 */
const _burnSubtitlesReplicate = functions.httpsCallable('burnSubtitlesReplicate');
export const burnSubtitlesReplicate = async (data: any) => {
    const res = await _burnSubtitlesReplicate(data);
    trackReplicateUsage(1).catch(() => {});
    return res;
};

/**
 * Cancel a Replicate Prediction Job
 */
export const cancelReplicateJob = functions.httpsCallable('cancelReplicateJob');

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Generic retry helper with exponential backoff
 * 
 * Supports two signatures for backward compatibility:
 * 1. withRetry(fn, { retries, delay, onRetry }) - new signature
 * 2. withRetry(fn, onRetry, retries, delay) - legacy signature
 * 
 * @param fn - The async function to retry
 * @param optionsOrOnRetry - Configuration options or legacy onRetry callback
 * @param legacyRetries - (legacy) Number of retries
 * @param legacyDelay - (legacy) Initial delay in ms
 * @returns Promise resolving to the function result
 * @throws Last error if all retries fail
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    optionsOrOnRetry?: {
        retries?: number;
        delay?: number;
        onRetry?: (attempt: number, error: Error) => void;
    } | ((attempt: number, error: Error) => void),
    legacyRetries?: number,
    legacyDelay?: number
): Promise<T> {
    // Handle legacy signature: withRetry(fn, onRetry, retries, delay)
    let retries: number;
    let initialDelay: number;
    let onRetry: ((attempt: number, error: Error) => void) | undefined;

    if (typeof optionsOrOnRetry === 'function') {
        // Legacy signature
        onRetry = optionsOrOnRetry;
        retries = legacyRetries ?? 2;
        initialDelay = legacyDelay ?? 2000;
    } else {
        // New signature
        const options = optionsOrOnRetry || {};
        retries = options.retries ?? 2;
        initialDelay = options.delay ?? 2000;
        onRetry = options.onRetry;
    }

    let lastError: Error | undefined;
    let delay = initialDelay;

    for (let i = 0; i < retries + 1; i++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error as Error;
            if (i < retries) {
                onRetry?.(i + 1, lastError);
                await new Promise((resolve) => setTimeout(resolve, delay));
                delay *= 2; // Exponential backoff
            }
        }
    }
    throw lastError;
}
